import { logger } from '../logger';
import { getHotelId } from '../supabase/configClient';
import { v4 as uuidv4 } from 'uuid';
import { supabaseClient } from '../supabase';

let analyticsServiceInstance: any = null;

class SessionTracker {
  private static instance: SessionTracker;
  private sessionStartTime: number;
  private lastActivityTime: number;
  private pageViews: number = 0;
  private interactions: number = 0;
  private isTracking: boolean = false;
  private inactivityTimeout: number = 30 * 60 * 1000; // 30 minutes
  private sessionExpiryTimeout: number = 4 * 60 * 60 * 1000; // 4 hours
  private inactivityTimer: NodeJS.Timeout | null = null;
  private expiryTimer: NodeJS.Timeout | null = null;
  private sessionId: string;
  private anonymousId: string;
  private language: string | null = null;
  private ipAddress: string | null = null;
  private previousLanguage: string | null = null;
  private hasSessionEnded: boolean = false;
  private hotelId: string;
  private hotelSlug: string | null = null;
  private sessionStorageKey: string = 'active_session_id';
  private sessionStorageTimestampKey: string = 'session_last_active';
  private sessionCheckInterval: NodeJS.Timeout | null = null;
  private readonly SESSION_CHECK_INTERVAL = 10000; // 10 seconds

  private constructor() {
    this.sessionStartTime = Date.now();
    this.lastActivityTime = Date.now();
    this.sessionId = this.generateSessionId();
    this.anonymousId = this.getOrCreateAnonymousId();
    this.hotelId = getHotelId();
    this.setupEventListeners();
    this.captureIpAddress();
    this.checkForDuplicateSessions();
    this.startExpiryTimer();
  }

  public static getInstance(): SessionTracker {
    if (!SessionTracker.instance) {
      SessionTracker.instance = new SessionTracker();
    }
    return SessionTracker.instance;
  }

  public setAnalyticsService(service: any): void {
    analyticsServiceInstance = service;
  }

  public setHotelInfo(hotelId: string, hotelSlug: string | null): void {
    this.hotelId = hotelId;
    this.hotelSlug = hotelSlug;
    
    logger.debug('Session tracker hotel info updated', {
      category: 'Analytics',
      data: { 
        hotelId,
        hotelSlug,
        sessionId: this.sessionId
      }
    });
  }

  private generateSessionId(): string {
    return uuidv4();
  }

  private getOrCreateAnonymousId(): string {
    try {
      const storedId = localStorage.getItem('anonymous_id');
      if (storedId) return storedId;
      const newId = uuidv4();
      localStorage.setItem('anonymous_id', newId);
      return newId;
    } catch {
      return uuidv4();
    }
  }

  private async captureIpAddress(): Promise<void> {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      if (!response.ok) throw new Error(`Failed to fetch IP: ${response.status}`);
      const data = await response.json();
      this.ipAddress = data.ip;
      logger.debug('Captured IP address', {
        category: 'Analytics',
        data: { ipAddress: this.ipAddress }
      });
    } catch (error) {
      logger.error('Failed to capture IP address', {
        category: 'Analytics',
        data: { error }
      });
      this.ipAddress = null;
    }
  }

  private setupEventListeners(): void {
    ['click', 'touchstart', 'mousemove', 'keypress', 'scroll'].forEach(eventType => {
      window.addEventListener(eventType, this.handleUserActivity.bind(this), { passive: true });
    });

    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this));
    window.addEventListener('storage', this.handleStorageChange.bind(this));
  }

  private handleUserActivity(): void {
    this.lastActivityTime = Date.now();
    this.interactions++;
    
    // Update last active timestamp in localStorage
    try {
      localStorage.setItem(this.sessionStorageTimestampKey, this.lastActivityTime.toString());
    } catch (error) {
      logger.error('Error updating session timestamp in localStorage', {
        category: 'Analytics',
        data: { error }
      });
    }
    
    // Reset inactivity timer
    if (this.inactivityTimer) clearTimeout(this.inactivityTimer);
    this.inactivityTimer = setTimeout(() => this.trackInactivity(), this.inactivityTimeout);
  }

  private handleVisibilityChange(): void {
    if (document.visibilityState === 'hidden') {
      if (!this.hasSessionEnded) this.trackSessionPause();
    } else {
      this.trackSessionResume();
    }
  }

  private handleBeforeUnload(): void {
    if (!this.hasSessionEnded) {
      logger.debug('Session ending due to page unload', {
        category: 'Analytics',
        data: { sessionId: this.sessionId }
      });
      this.trackSessionEnd('page_unload');
    }
  }

  private handleStorageChange(event: StorageEvent): void {
    // Check if another tab has updated the active session
    if (event.key === this.sessionStorageKey && event.newValue !== this.sessionId) {
      logger.debug('Detected session change in another tab', {
        category: 'Analytics',
        data: { 
          currentSessionId: this.sessionId,
          newSessionId: event.newValue
        }
      });
      
      // If this is a newer session, end the current one
      if (event.newValue) {
        const otherTabTimestamp = localStorage.getItem(this.sessionStorageTimestampKey);
        const otherTabTime = otherTabTimestamp ? parseInt(otherTabTimestamp, 10) : 0;
        
        // If the other tab has a more recent activity, end this session
        if (otherTabTime > this.lastActivityTime) {
          logger.debug('Ending session due to more recent activity in another tab', {
            category: 'Analytics',
            data: { 
              currentSessionId: this.sessionId,
              currentLastActivity: this.lastActivityTime,
              otherTabLastActivity: otherTabTime
            }
          });
          
          this.trackSessionEnd('duplicate_session');
        }
      }
    }
  }

  private checkForDuplicateSessions(): void {
    try {
      // Check if there's already an active session in localStorage
      const activeSessionId = localStorage.getItem(this.sessionStorageKey);
      const sessionTimestamp = localStorage.getItem(this.sessionStorageTimestampKey);
      
      if (activeSessionId && sessionTimestamp) {
        const lastActive = parseInt(sessionTimestamp, 10);
        const now = Date.now();
        
        // If the existing session is recent (less than 30 minutes old)
        if (now - lastActive < this.inactivityTimeout) {
          logger.debug('Found existing active session', {
            category: 'Analytics',
            data: { 
              existingSessionId: activeSessionId,
              lastActive: new Date(lastActive).toISOString(),
              timeSinceLastActive: (now - lastActive) / 1000,
              currentSessionId: this.sessionId
            }
          });
          
          // Use the existing session ID if it's recent
          this.sessionId = activeSessionId;
          this.sessionStartTime = lastActive;
        } else {
          // The existing session is stale, replace it
          logger.debug('Found stale session, replacing', {
            category: 'Analytics',
            data: { 
              staleSessionId: activeSessionId,
              lastActive: new Date(lastActive).toISOString(),
              timeSinceLastActive: (now - lastActive) / 1000
            }
          });
          
          localStorage.setItem(this.sessionStorageKey, this.sessionId);
          localStorage.setItem(this.sessionStorageTimestampKey, now.toString());
        }
      } else {
        // No existing session, store the current one
        localStorage.setItem(this.sessionStorageKey, this.sessionId);
        localStorage.setItem(this.sessionStorageTimestampKey, this.lastActivityTime.toString());
      }
      
      // Start periodic check for session consistency
      this.startSessionCheck();
      
    } catch (error) {
      logger.error('Error checking for duplicate sessions', {
        category: 'Analytics',
        data: { error }
      });
    }
  }

  private startSessionCheck(): void {
    // Clear any existing interval
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
    }
    
    // Set up interval to periodically check session status
    this.sessionCheckInterval = setInterval(() => {
      try {
        // Update the timestamp to show this session is active
        localStorage.setItem(this.sessionStorageTimestampKey, this.lastActivityTime.toString());
        
        // Ensure this session is the active one
        const activeSessionId = localStorage.getItem(this.sessionStorageKey);
        if (activeSessionId !== this.sessionId) {
          localStorage.setItem(this.sessionStorageKey, this.sessionId);
          
          logger.debug('Restored session as active', {
            category: 'Analytics',
            data: { 
              sessionId: this.sessionId,
              previousActiveSession: activeSessionId
            }
          });
        }
      } catch (error) {
        logger.error('Error in session check interval', {
          category: 'Analytics',
          data: { error }
        });
      }
    }, this.SESSION_CHECK_INTERVAL);
  }

  private startExpiryTimer(): void {
    // Clear any existing timer
    if (this.expiryTimer) {
      clearTimeout(this.expiryTimer);
    }
    
    // Set up timer to automatically end session after the expiry timeout
    this.expiryTimer = setTimeout(() => {
      if (!this.hasSessionEnded) {
        logger.debug('Session expired due to maximum duration', {
          category: 'Analytics',
          data: { 
            sessionId: this.sessionId,
            sessionDuration: (Date.now() - this.sessionStartTime) / 1000,
            expiryTimeout: this.sessionExpiryTimeout / 1000
          }
        });
        
        this.trackSessionEnd('session_timeout');
      }
    }, this.sessionExpiryTimeout);
  }

  private async trackInactivity(): Promise<void> {
    if (!analyticsServiceInstance) return;
    const inactiveTime = (Date.now() - this.lastActivityTime) / 1000;
    analyticsServiceInstance.trackEvent({
      event_type: 'session',
      event_name: 'user_inactivity',
      event_data: {
        hotel_id: this.hotelId,
        hotel_slug: this.hotelSlug,
        session_id: this.sessionId,
        anonymous_id: this.anonymousId,
        inactive_time: inactiveTime,
        last_active_path: window.location.pathname,
        timestamp: new Date().toISOString()
      }
    });
    
    // If inactivity exceeds 2 hours, end the session
    if (inactiveTime > 7200) { // 2 hours in seconds
      logger.debug('Ending session due to extended inactivity', {
        category: 'Analytics',
        data: { 
          sessionId: this.sessionId,
          inactiveTime
        }
      });
      
      this.trackSessionEnd('inactivity');
    }
  }

  private async trackSessionPause(): Promise<void> {
    if (!analyticsServiceInstance) return;
    const sessionDuration = (Date.now() - this.sessionStartTime) / 1000;
    analyticsServiceInstance.trackEvent({
      event_type: 'session',
      event_name: 'session_pause',
      event_data: {
        hotel_id: this.hotelId,
        hotel_slug: this.hotelSlug,
        session_id: this.sessionId,
        anonymous_id: this.anonymousId,
        duration: sessionDuration,
        page_views: this.pageViews,
        interactions: this.interactions,
        language: this.language,
        timestamp: new Date().toISOString()
      }
    });
  }

  private async trackSessionResume(): Promise<void> {
    if (!analyticsServiceInstance) return;
    analyticsServiceInstance.trackEvent({
      event_type: 'session',
      event_name: 'session_resume',
      event_data: {
        hotel_id: this.hotelId,
        hotel_slug: this.hotelSlug,
        session_id: this.sessionId,
        anonymous_id: this.anonymousId,
        pause_duration: (Date.now() - this.lastActivityTime) / 1000,
        timestamp: new Date().toISOString()
      }
    });
    this.lastActivityTime = Date.now();
    
    // Update localStorage with current timestamp
    try {
      localStorage.setItem(this.sessionStorageTimestampKey, this.lastActivityTime.toString());
    } catch (error) {
      logger.error('Error updating session timestamp in localStorage', {
        category: 'Analytics',
        data: { error }
      });
    }
  }

  private async trackSessionEnd(reason: string = 'normal'): Promise<void> {
    if (this.hasSessionEnded || !analyticsServiceInstance) return;
    this.hasSessionEnded = true;
    const sessionDuration = (Date.now() - this.sessionStartTime) / 1000;
    
    // Clear timers
    if (this.inactivityTimer) clearTimeout(this.inactivityTimer);
    if (this.expiryTimer) clearTimeout(this.expiryTimer);
    if (this.sessionCheckInterval) clearInterval(this.sessionCheckInterval);
    
    // Remove from localStorage if this is the active session
    try {
      const activeSessionId = localStorage.getItem(this.sessionStorageKey);
      if (activeSessionId === this.sessionId) {
        localStorage.removeItem(this.sessionStorageKey);
        localStorage.removeItem(this.sessionStorageTimestampKey);
      }
    } catch (error) {
      logger.error('Error cleaning up session from localStorage', {
        category: 'Analytics',
        data: { error }
      });
    }
    
    // Track session end event
    analyticsServiceInstance.trackEvent({
      event_type: 'session',
      event_name: 'session_end',
      event_data: {
        hotel_id: this.hotelId,
        hotel_slug: this.hotelSlug,
        session_id: this.sessionId,
        anonymous_id: this.anonymousId,
        duration: sessionDuration,
        page_views: this.pageViews,
        interactions: this.interactions,
        language: this.language,
        end_reason: reason,
        timestamp: new Date().toISOString()
      }
    });
    
    // Update session record in database
    try {
      const { error } = await supabaseClient
        .from('sessions')
        .update({ 
          end_time: new Date().toISOString(),
          duration: Math.round(sessionDuration),
          page_views: this.pageViews,
          interactions: this.interactions,
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', this.sessionId);
      
      if (error) {
        logger.error('Error updating session end in database', {
          category: 'Analytics',
          data: { error, sessionId: this.sessionId }
        });
      }
    } catch (error) {
      logger.error('Error updating session end in database', {
        category: 'Analytics',
        data: { error, sessionId: this.sessionId }
      });
    }
  }

  public incrementPageView(): void {
    this.pageViews++;
    this.handleUserActivity(); // Reset inactivity timer
  }

  public incrementInteraction(): void {
    this.interactions++;
    this.handleUserActivity(); // Reset inactivity timer
  }

  public getSessionId(): string {
    return this.sessionId;
  }

  public getAnonymousId(): string {
    return this.anonymousId;
  }

  public async updateLanguage(language: string): Promise<void> {
    if (!language || language === this.language) return;
    this.previousLanguage = this.language;
    this.language = language;
    
    // Track language change event
    if (analyticsServiceInstance && this.previousLanguage) {
      analyticsServiceInstance.trackEvent({
        event_type: 'user',
        event_name: 'language_change',
        event_data: {
          from_language: this.previousLanguage,
          to_language: language,
          hotel_id: this.hotelId,
          hotel_slug: this.hotelSlug,
          timestamp: new Date().toISOString()
        }
      });
    }
    
    // Throttle database updates to avoid excessive writes
    // Only update if language has been set for at least 5 seconds
    if (this.language) {
      try {
        const { error } = await supabaseClient
          .from('sessions')
          .update({ language: language, updated_at: new Date().toISOString() })
          .eq('id', this.sessionId);
          
        if (error) {
          logger.error('Error updating session language', {
            category: 'Analytics',
            data: { error, sessionId: this.sessionId, language }
          });
        }
      } catch (error) {
        logger.error('Error updating session language', {
          category: 'Analytics',
          data: { error, sessionId: this.sessionId, language }
        });
      }
    }
  }

  public async startTracking(): Promise<void> {
    if (this.isTracking) return;
    this.isTracking = true;
    this.sessionStartTime = Date.now();
    this.lastActivityTime = Date.now();
    this.pageViews = 0;
    this.interactions = 0;
    this.hasSessionEnded = false;

    // Get hotel info from window.HOTEL_CONFIG if available
    if (window.HOTEL_CONFIG?.id) {
      this.hotelId = window.HOTEL_CONFIG.id;
      this.hotelSlug = window.HOTEL_CONFIG.slug;
    }

    const i18nextLng = localStorage.getItem('i18nextLng');
    if (i18nextLng) {
      this.language = i18nextLng.split('-')[0].toLowerCase();
    }

    if (!this.ipAddress) {
      await this.captureIpAddress();
    }

    try {
      await supabaseClient
        .from('sessions')
        .insert({
          id: this.sessionId,
          hotel_id: this.hotelId,
          anonymous_id: this.anonymousId,
          start_time: new Date().toISOString(),
          referrer: document.referrer || 'direct',
          is_returning_user: localStorage.getItem('has_visited') === 'true',
          device_type: this.getDeviceType(),
          browser: this.getBrowser(),
          os: this.getOS(),
          screen_size: `${window.innerWidth}x${window.innerHeight}`,
          is_pwa: window.matchMedia('(display-mode: standalone)').matches,
          language: this.language,
          ip_address: this.ipAddress,
          is_active: true
        });
    } catch (error) {
      logger.error('Error creating session record', {
        category: 'Analytics',
        data: { error, sessionId: this.sessionId }
      });
    }

    if (analyticsServiceInstance) {
      analyticsServiceInstance.trackEvent({
        event_type: 'session',
        event_name: 'session_start',
        event_data: {
          hotel_id: this.hotelId,
          hotel_slug: this.hotelSlug,
          session_id: this.sessionId,
          anonymous_id: this.anonymousId,
          referrer: document.referrer || 'direct',
          is_returning_user: localStorage.getItem('has_visited') === 'true',
          device_type: this.getDeviceType(),
          browser: this.getBrowser(),
          os: this.getOS(),
          screen_size: `${window.innerWidth}x${window.innerHeight}`,
          is_pwa: window.matchMedia('(display-mode: standalone)').matches,
          language: this.language,
          ip_address: this.ipAddress,
          timestamp: new Date().toISOString()
        }
      });
    }

    this.inactivityTimer = setTimeout(() => {
      this.trackInactivity();
    }, this.inactivityTimeout);
    
    // Start expiry timer
    this.startExpiryTimer();
  }

  private getDeviceType(): string {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  private getBrowser(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('SamsungBrowser')) return 'Samsung Browser';
    if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
    if (ua.includes('Edg')) return 'Edge';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Safari')) return 'Safari';
    return 'Unknown';
  }

  private getOS(): string {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
    if (/Android/.test(ua)) return 'Android';
    if (/Windows/.test(ua)) return 'Windows';
    if (/Mac OS X/.test(ua)) return 'MacOS';
    if (/Linux/.test(ua)) return 'Linux';
    return 'Unknown';
  }
}

export const sessionTracker = SessionTracker.getInstance();